import React from 'react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@roger/ui-components';
import {
  type EnabledTabs,
  DEFAULT_ENABLED_TABS,
} from '../constants/intakeTabs';

export interface TabStatuses {
  extraction?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
  eligibility?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
  prior_auth?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
  ready_to_call?:
    'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
  scheduling?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
  push_to_emr?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
}

interface StepStatusIndicatorProps {
  tabStatuses?: TabStatuses | null;
  enabledTabs?: EnabledTabs;
  className?: string;
}

const STEPS: { key: keyof TabStatuses; label: string; shortLabel: string }[] = [
  { key: 'extraction', label: 'Extraction', shortLabel: 'Ext' },
  { key: 'eligibility', label: 'Eligibility', shortLabel: 'Elig' },
  { key: 'prior_auth', label: 'Prior Auth', shortLabel: 'Auth' },
  { key: 'ready_to_call', label: 'Ready to Call', shortLabel: 'Call' },
  { key: 'scheduling', label: 'Scheduling', shortLabel: 'Sched' },
  { key: 'push_to_emr', label: 'Push to EMR', shortLabel: 'EMR' },
];

const StepStatusIndicator: React.FC<StepStatusIndicatorProps> = ({
  tabStatuses,
  enabledTabs = DEFAULT_ENABLED_TABS,
  className = '',
}) => {
  // Filter steps based on enabled tabs
  const visibleSteps = STEPS.filter(step => {
    if (step.key === 'extraction') return true; // Always show extraction
    return enabledTabs[step.key] !== false;
  });

  const getStepStatus = (stepKey: keyof TabStatuses) => {
    const status = tabStatuses?.[stepKey];
    if (status === 'complete' || status === 'complete_with_errors') {
      return 'complete';
    }
    if (status === 'skipped') {
      return 'skipped';
    }
    // Check if previous steps are complete (for accessibility indication)
    const stepIndex = visibleSteps.findIndex(s => s.key === stepKey);
    if (stepIndex === 0) {
      return 'incomplete';
    }
    // Check if all previous steps are complete
    const previousSteps = visibleSteps.slice(0, stepIndex);
    const allPreviousComplete = previousSteps.every(s => {
      const prevStatus = tabStatuses?.[s.key];
      return (
        prevStatus === 'complete' ||
        prevStatus === 'complete_with_errors' ||
        prevStatus === 'skipped'
      );
    });
    return allPreviousComplete ? 'incomplete' : 'not_accessible';
  };

  const getCircleStyles = (
    status: 'complete' | 'incomplete' | 'not_accessible' | 'skipped'
  ) => {
    switch (status) {
      case 'complete':
        return 'bg-emerald-500';
      case 'incomplete':
        return 'bg-amber-500';
      case 'skipped':
        return 'bg-gray-400';
      case 'not_accessible':
      default:
        return 'bg-gray-200';
    }
  };

  const getStatusLabel = (
    status: 'complete' | 'incomplete' | 'not_accessible' | 'skipped'
  ) => {
    switch (status) {
      case 'complete':
        return 'Complete';
      case 'incomplete':
        return 'Incomplete';
      case 'skipped':
        return 'Skipped';
      case 'not_accessible':
      default:
        return 'Incomplete';
    }
  };

  // Get list of pending (incomplete) steps
  const pendingSteps = visibleSteps.filter(step => {
    const status = getStepStatus(step.key);
    return status === 'incomplete';
  });

  // Get the first pending step for display
  const nextPendingStep = pendingSteps[0];

  // Check if all steps are complete
  const allComplete = visibleSteps.every(
    step => getStepStatus(step.key) === 'complete'
  );

  return (
    <TooltipProvider
      delayDuration={0}
      skipDelayDuration={0}
      disableHoverableContent
    >
      <div className={`flex flex-col gap-0.5 ${className}`}>
        {/* Dots row */}
        <div className='flex items-center'>
          {visibleSteps.map(step => {
            const status = getStepStatus(step.key);
            return (
              <Tooltip key={step.key}>
                <TooltipTrigger asChild>
                  {/* Large hover target that overlaps with neighbors */}
                  <div
                    className='relative flex items-center justify-center cursor-default'
                    style={{ width: '16px', height: '16px' }}
                  >
                    <div
                      className={`
                        w-2 h-2 rounded-full
                        ${getCircleStyles(status)}
                      `}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side='bottom'
                  className='text-xs'
                  sideOffset={4}
                >
                  <div className='font-medium'>{step.label}</div>
                  <div className='text-muted-foreground'>
                    {getStatusLabel(status)}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        {/* Status text */}
        <div className='text-[10px] text-muted-foreground leading-tight'>
          {allComplete ? (
            <span className='text-emerald-600'>Complete</span>
          ) : nextPendingStep ? (
            <span>
              Pending:{' '}
              <span className='font-medium'>{nextPendingStep.shortLabel}</span>
            </span>
          ) : null}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default StepStatusIndicator;
